import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MapPin, ChevronDown, Bell, Search, Utensils, ShoppingBag, Pill, ShoppingCart, Heart, Star, Clock, ChevronRight, Home, CreditCard, HelpCircle, LogOut, Settings, User, Hand, Plus, RotateCcw, Gift, Phone, Coffee, Store, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';
import { CATEGORIES, BUSINESS_TYPES, IMAGES, money, CONTACT, telHref, waHref, WA_INCIDENT_MSG, SHOW_WHATSAPP } from '../lib/constants';
import { Screen, TopBar, SectionTitle, PrimaryButton } from './PhoneFrame';
import { AvatarPicker } from '../components/ImageUpload';
import { WhatsAppIcon } from '../components/WhatsAppIcon';
import { deliveryPrice, selectedAddress } from '../lib/deliveryPricing';

const ICONS = { Utensils, ShoppingBag, Pill, ShoppingCart, Gift, Coffee, Store };

export const RestaurantCard = ({ r, wide = false }) => {
  const instanceId = React.useId().replace(/:/g, '');
  const navigate = useNavigate();
  const { favorites, toggleFavorite, addresses, checkout, coverage, deliveryPricing, pricingReady } = useApp();
  const pricing = pricingReady ? deliveryPrice(r.deliveryFee, selectedAddress(addresses, checkout.addressId), coverage, deliveryPricing) : null;
  const fav = favorites.includes(r.id);
  return (
    <div data-testid={`rest-card-${r.id}`} onClick={() => navigate(`/app/restaurant/${r.id}`)} className={`card-premium overflow-hidden cursor-pointer ${wide ? 'w-full flex' : 'w-[168px] shrink-0'}`}>
      <div className={`relative overflow-hidden ${wide ? 'w-28 h-28 shrink-0' : 'h-[108px]'}`}>
        <img src={r.image} alt={r.name} className="w-full h-full object-cover" />
        {!wide && <span className="absolute top-2 left-2 bg-white/95 backdrop-blur text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-sm"><Star size={10} className="fill-amber-400 text-amber-400" /> {r.rating}</span>}
        {pricing?.deliveryFee === 0 && <span className="absolute bottom-2 left-2 bg-[#22A45D] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">Envío GRATIS</span>}
        {!r.open && <div className="absolute inset-0 bg-black/55 flex items-center justify-center text-white text-xs font-bold">Cerrado</div>}
      </div>
      <div className="p-3 flex-1 min-w-0">
        <div className="flex items-start justify-between gap-1">
          <p className="font-extrabold text-[13.5px] leading-tight text-[#1E2130] truncate">{r.name}</p>
          <button data-testid={`fav-${r.id}`} onClick={(e) => { e.stopPropagation(); toggleFavorite(r.id); }} className="shrink-0 press"><Heart size={16} className={fav ? 'fill-[#EE3B36] text-[#EE3B36]' : 'text-gray-300'} /></button>
        </div>
        <p className="text-[11px] text-gray-500 mt-1 truncate">{r.category}{r.pickup ? ' • Recoger en tienda' : ''}</p>
        <p className="text-[11px] text-gray-600 mt-1.5 flex items-center gap-1.5 whitespace-nowrap">
          {wide && <><Star size={11} className="fill-amber-400 text-amber-400" /> {r.rating}<span className="w-1 h-1 rounded-full bg-gray-300" /></>}
          <Clock size={11} /> {r.time}
          <span className="w-1 h-1 rounded-full bg-gray-300" />
          <span data-testid={`restaurant-card-fee-${r.id}-${instanceId}`} className={pricing?.deliveryFee === 0 ? 'text-[#22A45D] font-bold' : ''}>{pricing ? (pricing.deliveryFee === 0 ? 'GRATIS' : money(pricing.deliveryFee)) : '…'}</span>
        </p>
      </div>
    </div>
  );
};

const PROMOS = [
  { id: 'p1', title: '30% OFF en combos familiares', sub: 'Código TOCOA30 • solo hoy', badge: 'Oferta especial', image: IMAGES.combo, to: '/app/restaurants' },
  { id: 'p2', title: 'Envío GRATIS en hamburguesas', sub: 'En pedidos mayores a L 200', badge: 'Envío gratis', image: IMAGES.burger, to: '/app/restaurants' },
  { id: 'p3', title: 'Mandaditos 7 en minutos', sub: 'Farmacia, súper, paquetes y más', badge: 'Nuevo', image: IMAGES.rider2, to: '/app/mandaditos/new' },
  { id: 'p4', title: '2x1 en bebidas y postres', sub: 'Restaurantes seleccionados', badge: 'Promo 2x1', image: IMAGES.cola, to: '/app/restaurants' },
  { id: 'p5', title: 'Pizza familiar + refresco', sub: 'Desde L 249 con envío incluido', badge: 'Combo', image: IMAGES.pizza, to: '/app/restaurants' },
];

const PromoCarousel = () => {
  const navigate = useNavigate();
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % PROMOS.length), 4200);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="mt-5" data-testid="promo-carousel">
      <div className="mx-4 relative h-[148px] rounded-[22px] overflow-hidden shadow-[0_10px_30px_-12px_rgba(30,33,48,0.35)]">
        {PROMOS.map((p, idx) => (
          <button
            key={p.id}
            data-testid={`promo-${p.id}`}
            onClick={() => navigate(p.to)}
            aria-hidden={idx !== i}
            className="absolute inset-0 text-left transition-opacity duration-700"
            style={{ opacity: idx === i ? 1 : 0, pointerEvents: idx === i ? 'auto' : 'none' }}
          >
            <img src={p.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <span className="absolute inset-0 scrim-red" />
            <span className="absolute inset-0 shimmer" />
            <span className="relative z-10 flex flex-col h-full justify-center p-4 pr-24 text-white">
              <span className="self-start bg-white/20 backdrop-blur text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full">{p.badge}</span>
              <span className="text-[19px] font-extrabold leading-tight mt-2 drop-shadow">{p.title}</span>
              <span className="text-[11.5px] text-white/90 mt-1">{p.sub}</span>
            </span>
          </button>
        ))}
      </div>
      <div className="flex justify-center gap-1.5 mt-2.5">
        {PROMOS.map((p, idx) => (
          <button key={p.id} onClick={() => setI(idx)} aria-label={`Banner ${idx + 1}`} className={`h-1.5 rounded-full transition-all ${idx === i ? 'w-5 bg-[#EE3B36]' : 'w-1.5 bg-gray-300'}`} />
        ))}
      </div>
    </div>
  );
};

const useCountdown = () => {
  const [left, setLeft] = useState('');
  useEffect(() => {
    const tick = () => {
      const end = new Date();
      end.setHours(23, 59, 59, 999);
      const s = Math.max(0, Math.floor((end - Date.now()) / 1000));
      const p = (n) => String(n).padStart(2, '0');
      setLeft(`${p(Math.floor(s / 3600))}:${p(Math.floor((s % 3600) / 60))}:${p(s % 60)}`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);
  return left;
};

const OfferOfTheDay = ({ dish }) => {
  const navigate = useNavigate();
  const left = useCountdown();
  if (!dish) return null;
  const off = Math.round(dish.price * 0.65);
  return (
    <div className="mx-4 mt-6 card-premium overflow-hidden" data-testid="offer-of-day">
      <div className="relative h-[132px]">
        <img src={dish.image} alt={dish.name} className="w-full h-full object-cover" />
        <span className="absolute inset-0 scrim-bottom" />
        <span className="absolute top-3 left-3 bg-[#EE3B36] text-white text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-lg">Oferta del día</span>
        <span data-testid="offer-countdown" className="absolute top-3 right-3 bg-black/55 backdrop-blur text-white text-[11px] font-bold px-2.5 py-1 rounded-full tabular-nums">Termina en {left}</span>
        <div className="absolute bottom-3 left-3 right-3 text-white">
          <p className="font-extrabold text-[17px] leading-tight truncate drop-shadow">{dish.name}</p>
          <p className="text-[11.5px] text-white/85 truncate">{dish.restaurant}</p>
        </div>
      </div>
      <div className="p-3 flex items-center gap-3">
        <div className="min-w-0">
          <p className="text-[20px] font-extrabold text-[#EE3B36] leading-none">{money(off)}</p>
          <p className="text-[11px] text-gray-400 line-through">{money(dish.price)}</p>
        </div>
        <span className="bg-[#E6F7F1] text-[#22A45D] text-[10px] font-bold px-2 py-1 rounded-full whitespace-nowrap">Ahorra {money(dish.price - off)}</span>
        <button data-testid="offer-cta" onClick={() => navigate(`/app/restaurant/${dish.restaurantId}`)} className="ml-auto h-10 px-4 rounded-xl bg-[#EE3B36] text-white text-[13px] font-bold press shadow-[0_6px_18px_rgba(238,59,54,0.35)] whitespace-nowrap">Pedir ahora</button>
      </div>
    </div>
  );
};

const DishCard = ({ dish }) => {
  const navigate = useNavigate();
  const { addToCart } = useApp();
  const hasOptions = !!(dish.options?.length || dish.sizes?.length);
  return (
    <div data-testid={`dish-${dish.id}`} className="card-premium w-[142px] shrink-0 overflow-hidden">
      <div className="relative h-[96px]">
        <img src={dish.image} alt={dish.name} className="w-full h-full object-cover" />
        <button
          data-testid={`add-dish-${dish.id}`}
          onClick={() => (hasOptions
            ? navigate(`/app/restaurant/${dish.restaurantId}`)
            : addToCart(dish.restaurantId, { id: dish.id, name: dish.name, price: dish.price, image: dish.image }))}
          className="absolute -bottom-3 right-2 w-9 h-9 rounded-full bg-[#EE3B36] text-white flex items-center justify-center shadow-lg press"
        ><Plus size={18} /></button>
      </div>
      <div className="p-2.5 pt-3">
        <p className="text-[12.5px] font-bold leading-tight truncate">{dish.name}</p>
        <p className="text-[10.5px] text-gray-500 truncate">{dish.restaurant}</p>
        <p className="text-[13px] font-extrabold text-[#1E2130] mt-1">{money(dish.price)}</p>
      </div>
    </div>
  );
};

export const HomeScreen = () => {
  const navigate = useNavigate();
  const { customer, restaurants, menus, customerOrders, cartCount, addresses, checkout, coverage, deliveryPricing, pricingReady, customerUnread } = useApp();
  const address = selectedAddress(addresses, checkout.addressId);
  const [q, setQ] = useState('');
  const active = restaurants.filter((r) => r.status === 'active');
  const featured = active.filter((r) => r.type === 'comida');
  const topRated = useMemo(() => [...active].sort((a, b) => (b.rating || 0) - (a.rating || 0)).slice(0, 6), [active]);
  const freeOrCheap = useMemo(() => [...active].sort((a, b) => (a.deliveryFee || 0) - (b.deliveryFee || 0)).slice(0, 6), [active]);
  const dishes = useMemo(() => {
    const out = [];
    Object.entries(menus || {}).forEach(([rid, m]) => {
      const r = restaurants.find((x) => x.id === rid);
      if (!r || r.status !== 'active') return;
      (m.items || []).filter((i) => i.active !== false && i.price).forEach((i) => out.push({ ...i, restaurantId: rid, restaurant: r.name, image: i.image || r.image }));
    });
    return out.sort((a, b) => b.price - a.price);
  }, [menus, restaurants]);
  const popular = dishes.slice(0, 10);
  const offer = dishes.find((d) => d.price >= 120) || dishes[0];
  const giftShops = useMemo(() => active.filter((r) => r.type === 'regalos'), [active]);
  const cafeterias = active.filter((r) => r.type === 'cafeteria');
  const tiendas = active.filter((r) => r.type === 'tienda');
  const reorder = useMemo(() => {
    const seen = new Set();
    return customerOrders.filter((o) => o.status === 'completed' && o.restaurantId && !seen.has(o.restaurantId) && seen.add(o.restaurantId)).slice(0, 5);
  }, [customerOrders]);
  const results = q ? active.filter((r) => r.name.toLowerCase().includes(q.toLowerCase()) || r.category.toLowerCase().includes(q.toLowerCase())) : null;

  return (
    <Screen nav>
      <div className="px-4 pt-2 flex items-center justify-between">
        <button className="flex items-center gap-1.5 text-[13px] font-bold press"><span className="w-7 h-7 rounded-full bg-[#FDECEC] text-[#EE3B36] flex items-center justify-center"><MapPin size={15} /></span> Tocoa, Colón <ChevronDown size={14} className="text-gray-400" /></button>
        <button data-testid="home-bell" className="relative p-1 press" onClick={() => navigate('/app/notificaciones')}><Bell size={22} />{customerUnread > 0 && <span data-testid="home-bell-badge" className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 bg-[#EE3B36] text-white text-[9px] font-bold rounded-full flex items-center justify-center">{customerUnread > 9 ? '9+' : customerUnread}</span>}</button>
      </div>
      <div className="px-4 mt-3">
        <h1 className="text-[23px] font-extrabold tracking-tight text-[#1E2130] flex items-center gap-2">Hola, {customer?.name} <Hand size={21} className="text-amber-400 fill-amber-200" /></h1>
        <p className="text-gray-500 text-[13.5px]">¿Qué necesitas hoy?</p>
      </div>
      <div className="px-4 mt-3">
        <div className="flex items-center gap-2 h-12 px-3.5 rounded-2xl bg-white border border-gray-100 shadow-[var(--shadow-soft)] focus-within:border-[#EE3B36]/40"><Search size={18} className="text-gray-400" /><input data-testid="home-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar restaurantes, comidas..." className="flex-1 bg-transparent outline-none text-sm" /></div>
      </div>

      {results ? (
        <div className="px-4 mt-4 space-y-3 stagger">
          <p className="text-sm text-gray-500">{results.length} resultados para "{q}"</p>
          {results.map((r) => <RestaurantCard key={r.id} r={r} wide />)}
        </div>
      ) : (
        <>
          <div className="px-4 mt-5 grid grid-cols-4 gap-2 stagger">
            {CATEGORIES.map((c) => {
              const I = ICONS[c.icon];
              return (
                <button key={c.id} data-testid={`cat-${c.id}`} onClick={() => (c.id === 'mandaditos' ? navigate('/app/mandaditos') : navigate('/app/restaurants', { state: { type: c.id } }))} className="flex flex-col items-center gap-1.5 press">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-[0_6px_16px_-8px_rgba(30,33,48,0.4)]" style={{ background: c.color, color: c.iconColor }}><I size={23} /></div>
                  <span className="text-[10.5px] font-semibold text-[#1E2130] leading-tight text-center">{c.name}</span>
                </button>
              );
            })}
          </div>

          <PromoCarousel />

          <OfferOfTheDay dish={offer} />

          {giftShops.length > 0 && (
            <div className="mt-6" data-testid="gift-section">
              <SectionTitle title="Flores y Regalos" action="Ver todos" onAction={() => navigate('/app/restaurants', { state: { type: 'regalos' } })} />
              <div className="mx-4 rounded-[22px] overflow-hidden relative h-[128px] mb-3 shadow-[0_10px_30px_-12px_rgba(214,51,108,0.4)]">
                <img src={giftShops[0].image} alt="" className="absolute inset-0 w-full h-full object-cover" />
                <span className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(157,23,77,0.92) 0%, rgba(214,51,108,0.55) 60%, transparent 100%)' }} />
                <div className="relative z-10 h-full flex flex-col justify-center p-4 pr-20 text-white">
                  <span className="self-start bg-white/25 backdrop-blur text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full">Nuevo</span>
                  <p className="font-extrabold text-[18px] leading-tight mt-2 drop-shadow">Flores, regalos y arreglos</p>
                  <p className="text-[11.5px] text-white/90 mt-0.5">Envíalos con dedicatoria a quien tú quieras</p>
                  <button data-testid="gift-banner-cta" onClick={() => navigate('/app/restaurants', { state: { type: 'regalos' } })} className="mt-2.5 self-start bg-white text-[#D6336C] font-bold text-xs px-4 py-2 rounded-xl press">Explorar</button>
                </div>
              </div>
              <div data-testid="gift-shops-row" className="flex gap-3 px-4 overflow-x-auto no-scrollbar pb-1 snap-x-row stagger">
                {giftShops.map((r) => <RestaurantCard key={r.id} r={r} />)}
              </div>
            </div>
          )}

          {reorder.length > 0 && (
            <div className="mt-6" data-testid="reorder-row">
              <SectionTitle title="Pedir de nuevo" action="Mis pedidos" onAction={() => navigate('/app/orders')} />
              <div className="flex gap-2 px-4 overflow-x-auto no-scrollbar snap-x-row">
                {reorder.map((o) => (
                  <button key={o.id} data-testid={`reorder-${o.id}`} onClick={() => navigate(`/app/restaurant/${o.restaurantId}`)} className="shrink-0 flex items-center gap-2 bg-white border border-gray-100 rounded-full pl-1.5 pr-3.5 py-1.5 shadow-[var(--shadow-soft)] press">
                    <img src={o.image || IMAGES.combo} alt="" className="w-8 h-8 rounded-full object-cover" />
                    <span className="text-left"><span className="block text-[12px] font-bold leading-none">{o.restaurant}</span><span className="block text-[10px] text-gray-500 mt-0.5">{o.date}</span></span>
                    <RotateCcw size={14} className="text-[#EE3B36]" />
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6">
            <SectionTitle title="Mejor calificados" action="Ver todos" onAction={() => navigate('/app/restaurants')} />
            <div data-testid="top-rated-row" className="flex gap-3 px-4 overflow-x-auto no-scrollbar pb-1 snap-x-row stagger">
              {topRated.map((r) => <RestaurantCard key={r.id} r={r} />)}
            </div>
          </div>

          <div className="mx-4 mt-6 rounded-[22px] bg-[#1E2130] text-white p-4 relative overflow-hidden h-[126px] shadow-[0_12px_30px_-14px_rgba(30,33,48,0.7)]">
            <div className="relative z-10 max-w-[64%]">
              <p className="font-extrabold text-[19px] leading-tight">Somos Mandaditos 7</p>
              <p className="text-[11.5px] text-white/75 mt-0.5">Escribe tu mandado y envíalo</p>
              <button data-testid="mandaditos-cta" onClick={() => navigate('/app/mandaditos/new')} className="mt-3 bg-[#EE3B36] text-white font-bold text-xs px-4 py-2 rounded-xl press shadow-[0_6px_18px_rgba(238,59,54,0.45)]">Solicitar ahora</button>
            </div>
            <img src={IMAGES.rider2} alt="" className="absolute right-0 top-0 h-full w-[48%] object-cover [mask-image:linear-gradient(to_right,transparent,black_45%)]" />
          </div>

          <div className="mt-6">
            <SectionTitle title="Envío gratis y cerca de ti" />
            <div data-testid="free-delivery-row" className="flex gap-3 px-4 overflow-x-auto no-scrollbar pb-1 snap-x-row">
              {freeOrCheap.map((r) => { const pricing = pricingReady ? deliveryPrice(r.deliveryFee, address, coverage, deliveryPricing) : null; return (
                <button key={r.id} data-testid={`near-${r.id}`} onClick={() => navigate(`/app/restaurant/${r.id}`)} className="card-premium shrink-0 w-[196px] p-3 flex items-center gap-3 text-left">
                  <img src={r.image} alt="" className="w-12 h-12 rounded-xl object-cover" />
                  <div className="min-w-0">
                    <p className="text-[12.5px] font-bold truncate">{r.name}</p>
                    <p className="text-[10.5px] text-gray-500 truncate">{r.time}</p>
                    <p data-testid={`near-fee-${r.id}`} className={`text-[10.5px] font-bold ${pricing?.deliveryFee === 0 ? 'text-[#22A45D]' : 'text-[#2F80ED]'}`}>{pricing ? (pricing.deliveryFee === 0 ? 'Envío GRATIS' : `Envío ${money(pricing.deliveryFee)}`) : '…'}</p>
                  </div>
                </button>
              ); })}
            </div>
          </div>

          {popular.length > 0 && (
            <div className="mt-6">
              <SectionTitle title="Platillos populares" action="Ver todos" onAction={() => navigate('/app/restaurants')} />
              <div data-testid="popular-dishes-row" className="flex gap-3 px-4 overflow-x-auto no-scrollbar pb-2 snap-x-row stagger">
                {popular.map((d) => <DishCard key={`${d.restaurantId}-${d.id}`} dish={d} />)}
              </div>
            </div>
          )}

          <div className="mt-6">
            <SectionTitle title="Restaurantes destacados" action="Ver todos" onAction={() => navigate('/app/restaurants', { state: { type: 'comida' } })} />
            <div className="flex gap-3 px-4 overflow-x-auto no-scrollbar pb-1 snap-x-row">
              {featured.map((r) => <RestaurantCard key={r.id} r={r} />)}
            </div>
          </div>

          {cafeterias.length > 0 && (
            <div className="mt-6" data-testid="cafeterias-section">
              <SectionTitle title="Cafeterías" action="Ver todas" onAction={() => navigate('/app/restaurants', { state: { type: 'cafeteria' } })} />
              <div className="flex gap-3 px-4 overflow-x-auto no-scrollbar pb-1 snap-x-row stagger">
                {cafeterias.map((r) => <RestaurantCard key={r.id} r={r} />)}
              </div>
            </div>
          )}

          {tiendas.length > 0 && (
            <div className="mt-6" data-testid="tiendas-section">
              <SectionTitle title="Tiendas" action="Ver todas" onAction={() => navigate('/app/restaurants', { state: { type: 'tienda' } })} />
              <div className="flex gap-3 px-4 overflow-x-auto no-scrollbar pb-1 snap-x-row stagger">
                {tiendas.map((r) => <RestaurantCard key={r.id} r={r} />)}
              </div>
            </div>
          )}

          <div className="mt-6">
            <SectionTitle title="Farmacia y supermercado" />
            <div className="px-4 space-y-3">
              {active.filter((r) => ['farmacia', 'supermercado'].includes(r.type)).map((r) => <RestaurantCard key={r.id} r={r} wide />)}
            </div>
          </div>

          <div className="mx-4 mt-7 mb-2 rounded-2xl bg-white border border-gray-100 p-4 text-center shadow-[var(--shadow-soft)]">
            <p className="font-script text-[26px] text-[#EE3B36] leading-none">Tocoa</p>
            <p className="text-[11.5px] text-gray-500 mt-1">Gracias por confiar en Delibery 7</p>
          </div>
        </>
      )}

      {cartCount > 0 && (
        <div className="absolute bottom-[78px] left-4 right-4 z-30">
          <PrimaryButton data-testid="view-cart-fab" onClick={() => navigate('/app/cart')} className="flex items-center justify-between px-4 shadow-xl"><span className="bg-white/25 rounded-md px-2 py-0.5 text-xs">{cartCount}</span><span>Ver carrito</span><ShoppingCart size={18} /></PrimaryButton>
        </div>
      )}
    </Screen>
  );
};

export const RestaurantList = () => {
  const { restaurants } = useApp();
  const { state } = useLocation();
  const [type, setType] = useState(state?.type || 'all');
  const active = restaurants.filter((r) => r.status === 'active' && (type === 'all' || r.type === type));
  const title = BUSINESS_TYPES.find((b) => b.v === type)?.l || 'Negocios';
  return (
    <Screen nav>
      <TopBar title={type === 'all' ? 'Negocios' : title} />
      <div className="flex gap-2 px-4 overflow-x-auto no-scrollbar mt-1">
        {BUSINESS_TYPES.map((c) => (
          <button key={c.v} data-testid={`rlist-filter-${c.v}`} onClick={() => setType(c.v)} className={`px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap ${type === c.v ? 'bg-[#EE3B36] text-white' : 'bg-gray-100 text-gray-600'}`}>{c.l}</button>
        ))}
      </div>
      <div className="px-4 mt-4 space-y-3">
        {active.map((r) => <RestaurantCard key={r.id} r={r} wide />)}
        {active.length === 0 && <p className="text-center text-sm text-gray-400 py-12">Aún no hay negocios activos en esta categoría</p>}
      </div>
    </Screen>
  );
};

export const Favorites = () => {
  const { restaurants, favorites } = useApp();
  const list = restaurants.filter((r) => favorites.includes(r.id));
  return (
    <Screen nav>
      <div className="px-4 pt-3"><h1 className="text-2xl font-extrabold">Favoritos</h1><p className="text-sm text-gray-500">Tus restaurantes guardados</p></div>
      <div className="px-4 mt-4 space-y-3">
        {list.length === 0 && <div className="text-center py-16 text-gray-400"><Heart size={40} className="mx-auto mb-2" /><p className="text-sm">Aún no tienes favoritos</p></div>}
        {list.map((r) => <RestaurantCard key={r.id} r={r} wide />)}
      </div>
    </Screen>
  );
};

export const DeleteAccountButton = () => {
  const navigate = useNavigate();
  const { deleteMyAccount, customer } = useApp();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const hasPassword = !!customer?.hasPassword;

  const submit = async () => {
    setBusy(true);
    try {
      await deleteMyAccount(password, confirm);
      setOpen(false);
      navigate('/app');
    } catch (e) { /* the context already shows the error toast */ }
    setBusy(false);
  };

  return (
    <>
      <button data-testid="delete-account-open" onClick={() => { setOpen(true); setPassword(''); setConfirm(''); }}
        className="w-full flex items-center gap-3 bg-white border border-gray-100 rounded-xl p-3 text-left text-gray-500 hover:bg-gray-50">
        <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center"><Trash2 size={20} /></div>
        <div className="flex-1"><p className="text-sm font-semibold">Eliminar mi cuenta</p><p className="text-xs text-gray-400">Borra tus datos de forma permanente</p></div>
        <ChevronRight size={18} className="text-gray-400" />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end" data-testid="delete-account-sheet">
          <button className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} aria-label="Cerrar" />
          <div className="relative w-full bg-white rounded-t-3xl p-5 pb-[calc(20px+var(--safe-bottom))] animate-in slide-in-from-bottom duration-200">
            <div className="w-11 h-11 rounded-2xl bg-red-50 text-[#EE3B36] flex items-center justify-center"><AlertTriangle size={22} /></div>
            <h3 className="text-lg font-extrabold mt-3">¿Eliminar tu cuenta?</h3>
            <p className="text-sm text-gray-500 mt-1.5">Esta acción no se puede deshacer. Se borran tu cuenta, tus direcciones, tu foto y tus favoritos. Los pedidos ya cobrados se conservan sin tu nombre ni tu dirección, por contabilidad.</p>
            {hasPassword ? (
              <div className="mt-4">
                <label className="text-xs font-semibold text-gray-600">Confirma tu contraseña</label>
                <input data-testid="delete-account-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="mt-1.5 w-full h-12 rounded-xl border border-gray-200 px-4 text-sm focus:outline-none focus:border-[#EE3B36]" placeholder="Tu contraseña" />
              </div>
            ) : (
              <div className="mt-4">
                <label className="text-xs font-semibold text-gray-600">Escribe ELIMINAR para confirmar</label>
                <input data-testid="delete-account-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  className="mt-1.5 w-full h-12 rounded-xl border border-gray-200 px-4 text-sm focus:outline-none focus:border-[#EE3B36]" placeholder="ELIMINAR" />
              </div>
            )}
            <div className="mt-5 grid grid-cols-2 gap-3">
              <button data-testid="delete-account-cancel" onClick={() => setOpen(false)} className="h-12 rounded-xl border border-gray-200 font-semibold text-sm">Cancelar</button>
              <button data-testid="delete-account-submit" onClick={submit} disabled={busy}
                className="h-12 rounded-xl bg-[#EE3B36] text-white font-semibold text-sm disabled:opacity-60">{busy ? 'Eliminando...' : 'Sí, eliminar'}</button>
            </div>
            <p className="text-[11px] text-gray-400 mt-4 text-center">{SHOW_WHATSAPP ? `También puedes pedirlo por WhatsApp al ${CONTACT.whatsapp}` : `También puedes pedirlo llamando al ${CONTACT.callCenter[0]}`}</p>
          </div>
        </div>
      )}
    </>
  );
};

export const Profile = () => {
  const navigate = useNavigate();
  const { customer, setCustomer, signOut, customerRecord, updateCustomer, customerOrders, addresses, favorites, customerUnread } = useApp();
  const items = [
    { icon: User, label: 'Mis datos', sub: customer?.phone, to: '/app/datos' },
    { icon: Home, label: 'Mis direcciones', sub: `${addresses.length} direcciones guardadas`, to: '/app/address' },
    { icon: CreditCard, label: 'Métodos de pago', sub: 'Transferencia bancaria', to: '/app/payment' },
    { icon: Bell, label: 'Notificaciones', sub: customerUnread > 0 ? `${customerUnread} sin leer` : 'Activadas', to: '/app/ajustes/notificaciones' },
    { icon: Settings, label: 'Configuración de cuenta', sub: 'Idioma, privacidad', to: '/app/ajustes' },
    { icon: HelpCircle, label: 'Ayuda y soporte', sub: SHOW_WHATSAPP ? 'Centro de llamadas · WhatsApp' : 'Centro de llamadas', to: '/app/support' },
  ];
  return (
    <Screen nav>
      <div className="bg-[#EE3B36] text-white px-4 pb-10 -mt-2 pt-6 rounded-b-[32px]">
        <div className="flex items-center gap-4">
          <AvatarPicker testId="customer-avatar-picker" value={customer?.avatar} folder="avatars" onChange={(url) => { setCustomer({ ...customer, avatar: url }); if (customerRecord) updateCustomer(customerRecord.id, { avatar: url }); }} />
          <div><p className="text-xl font-extrabold">{customer?.fullName}</p><p className="text-sm text-white/90">{customer?.phone}</p></div>
        </div>
      </div>
      <div className="mx-4 -mt-6 bg-white rounded-2xl shadow-md border border-gray-100 grid grid-cols-3 divide-x divide-gray-100">
        {[['Pedidos', customerOrders.length], ['Favoritos', favorites.length], ['Puntos', customerOrders.filter((o) => o.status === 'completed').length * 10]].map(([l, v]) => (<div key={l} className="py-3 text-center"><p className="font-extrabold text-lg">{v}</p><p className="text-[11px] text-gray-500">{l}</p></div>))}
      </div>
      <div className="px-4 mt-5 space-y-2">
        {items.map((it) => (
          <button key={it.label} onClick={() => (it.to ? navigate(it.to, { state: { fromProfile: true } }) : toast(`${it.label} - disponible con backend`))} className="w-full flex items-center gap-3 bg-white border border-gray-100 rounded-xl p-3 text-left hover:bg-gray-50">
            <div className="w-10 h-10 rounded-xl bg-[#FDECEC] text-[#EE3B36] flex items-center justify-center"><it.icon size={20} /></div>
            <div className="flex-1"><p className="text-sm font-semibold">{it.label}</p><p className="text-xs text-gray-500">{it.sub}</p></div>
            <ChevronRight size={18} className="text-gray-400" />
          </button>
        ))}
        <button data-testid="customer-logout" onClick={() => { signOut(); navigate('/app'); }} className="w-full flex items-center gap-3 bg-white border border-red-100 rounded-xl p-3 text-left text-red-600 hover:bg-red-50"><div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center"><LogOut size={20} /></div><p className="text-sm font-semibold">Cerrar sesión</p></button>
        <DeleteAccountButton />
      </div>
    </Screen>
  );
};

export const Support = () => {
  return (
    <Screen nav>
      <TopBar title="Ayuda y soporte" />
      <div className="px-4 pt-2 pb-4" data-testid="support-screen">
        <div className="rounded-2xl bg-gradient-to-br from-[#EE3B36] to-[#c22f2a] text-white p-4 shadow-lg shadow-red-500/25">
          <p className="text-[11px] font-bold uppercase tracking-wider text-white/80">Centro de llamadas</p>
          <p className="font-extrabold text-lg mt-0.5">Estamos para ayudarte</p>
          <p className="text-[12.5px] text-white/90 mt-1">{SHOW_WHATSAPP ? 'Llámanos o escríbenos por WhatsApp para reportar cualquier incidente con tu pedido.' : 'Llámanos para reportar cualquier incidente con tu pedido.'}</p>
        </div>

        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mt-5 mb-2 px-1">Líneas de atención</p>
        <div className="space-y-2">
          {CONTACT.callCenter.map((n, i) => (
            <a key={n} href={telHref(n)} data-testid={`call-number-${i}`} className="w-full flex items-center gap-3 bg-white border border-gray-100 rounded-xl p-3 hover:bg-gray-50 press">
              <div className="w-11 h-11 rounded-xl bg-[#FDECEC] text-[#EE3B36] flex items-center justify-center shrink-0"><Phone size={20} /></div>
              <div className="flex-1 min-w-0">
                <p className="text-[15px] font-bold text-[#1E2130]">{n}</p>
                <p className="text-[11.5px] text-gray-500">{i === 0 ? 'Línea principal' : 'Línea alternativa'}</p>
              </div>
              {i === 0 && <span className="text-[10px] font-bold text-[#EE3B36] bg-[#FDECEC] px-2 py-1 rounded-full">Principal</span>}
            </a>
          ))}
        </div>

        {SHOW_WHATSAPP && (
          <>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mt-5 mb-2 px-1">WhatsApp</p>
            <a href={waHref(CONTACT.whatsapp, WA_INCIDENT_MSG)} target="_blank" rel="noreferrer" data-testid="support-whatsapp-btn" className="w-full flex items-center gap-3 rounded-xl p-3.5 bg-[#25D366] text-white shadow-lg shadow-emerald-500/25 press">
              <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center shrink-0"><WhatsAppIcon className="w-6 h-6" /></div>
              <div className="flex-1 min-w-0 text-left">
                <p className="text-[15px] font-bold">Reportar un incidente</p>
                <p className="text-[11.5px] text-white/90">Escríbenos al {CONTACT.whatsapp}</p>
              </div>
              <ChevronRight size={20} className="text-white/80" />
            </a>
          </>
        )}
      </div>
    </Screen>
  );
};
